<template>
  <div class="">
    <StepHeader
      :title="title"
      :theme="theme"
    />

    <StepContent :theme="theme">
      <AppCurrencyInput
        :label="fields.label"
        :hint="fields.hint"
        :name="fields.name"
        :init-value="fields.value"
        :rules="fields.rules"
        :hide-error="!submitted"
        :theme="theme"
        :error="fields.error"
        @input="handleInput(fields.vuexAction, $event)"
      />
      <AppInputWithValidation
        v-if="incomeFrequency === 'Hourly'"
        :label="hoursInput.label"
        :hint="hoursInput.hint"
        :name="hoursInput.name"
        :type="hoursInput.type"
        :value="hoursInput.value"
        :rules="hoursInput.rules"
        :hide-error="!submitted"
        :max-length="hoursInput.maxLength"
        :error="hoursInput.error"
        :theme="theme"
        @input="hoursInput.handleInput"
      />
    </StepContent>

    <StepFooter hide-mobile>
      <AppButton
        :theme="theme"
        :loading="submitting"
        @click="handleContinue"
      >
        Continue
      </AppButton>
    </StepFooter>
  </div>
</template>

<script>
import AppButton from '@/components/common/buttons/AppButton'
import AppInputWithValidation from '@/components/common/inputs/AppInputWithValidation'
import StepContent from '@/components/common/step-layout/StepContent'
import StepFooter from '@/components/common/step-layout/StepFooter'
import StepHeader from '@/components/common/step-layout/StepHeader'
import navigation from '@/mixins/navigation'
import input from '@/mixins/input'
import submitLoans from '@/mixins/submit/submit-loans'
import AppCurrencyInput from '@/components/common/inputs/AppCurrencyInput'

export default {
  // Name
  name: 'IncomeDetailsAmount',

  // Components
  components: {
    AppButton,
    AppCurrencyInput,
    AppInputWithValidation,
    StepContent,
    StepFooter,
    StepHeader
  },

  // Mixins
  mixins: [
    input,
    submitLoans,
    navigation
  ],

  props: {
    theme: {
      type: String,
      default: 'blue'
    }
  },

  // Computed
  computed: {
    incomeFrequency () {
      return this.$store.state.employment.income_frequency
    },

    annualIncome () {
      return this.$store.state.employment.annual_income
    },

    annualIncomeError () {
      return this.$store.state.validations.annualIncomeError
    },

    monthlyIncomeError () {
      return this.$store.state.validations.monthlyIncomeError
    },

    hourlyIncome () {
      return this.$store.state.employment.hourly_income
    },

    hoursPerWeek () {
      return this.$store.state.employment.hours_per_week
    },

    monthlyIncome () {
      return this.$store.state.employment.monthly_income
    },

    hourlyIncomeError () {
      return this.$store.state.validations.hourlyIncomeError
    },

    hoursPerWeekError () {
      return this.$store.state.validations.hoursPerWeekError
    },

    title () {
      switch (this.incomeFrequency) {
        case ('Annual'):
          return 'What Is Your Annual Salary?\nBefore Taxes & Deductions'
        case ('Hourly'):
          return 'How Much Do You Get Paid Per Hour?'
        default:
          return 'What Is Your Monthly Income?\nBefore Taxes & Deductions'
      }
    },

    fields () {
      switch (this.incomeFrequency) {
        case ('Annual'):
          return this.fieldsAnnual
        case ('Hourly'):
          return this.fieldsHourly
        default:
          return this.fieldsMonthly
      }
    },

    fieldsAnnual () {
      return {
        label: 'Annual Salary',
        hint: 'Round to Nearest Dollar',
        name: 'income_value',
        value: this.annualIncome,
        rules: 'required:Annual Salary|min_currency:0|max_currency:9,999,999,999,999|max_digits:6',
        vuexAction: 'setAnnualIncome',
        error: this.annualIncomeError
      }
    },

    fieldsHourly () {
      return {
        label: 'Hourly Wage',
        hint: '',
        name: 'income_value',
        rules: 'required:Hourly Wage|min_currency:0|max_currency:999.99|max_digits:3',
        value: this.hourlyIncome,
        vuexAction: 'setHourlyIncome',
        error: this.hourlyIncomeError
      }
    },

    hoursInput () {
      return {
        label: 'Average Hours Per Week',
        name: 'income_hours',
        type: 'tel',
        value: this.hoursPerWeek,
        rules: 'required:Average Hours Per Week|numeric|min_value:1|max_value:168',
        maxLength: 3,
        error: this.hoursPerWeekError,
        handleInput: (value) => {
          value = value.replace(/[^0-9]/g, '')
          this.$_input_updateState('setHoursPerWeek', value)
          if (this.submitted) {
            this.$_navigation_validateStep()
          }
        }
      }
    },

    fieldsMonthly () {
      return {
        label: 'Monthly Income',
        hint: 'Round to Nearest Dollar',
        name: 'income_value',
        rules: 'required:Monthly Income|min_currency:0|max_currency:9,999,999,999,999|max_digits:5',
        value: this.monthlyIncome,
        vuexAction: 'setMonthlyIncome',
        error: this.monthlyIncomeError
      }
    },

    dataLayerObject () {
      switch (this.incomeFrequency) {
        case ('Annual'):
          return { event: `spring_${this.eventPrefix}_annual_income_continue`, annual_income: this.annualIncome }
        case ('Hourly'):
          return { event: `spring_${this.eventPrefix}_hourly_income_continue`, hourly_income: this.hourlyIncome }
        default:
          return { event: `spring_${this.eventPrefix}_monthly_income_continue`, monthly_income: this.monthlyIncome }
      }
    },

    updateLeadPayload () {
      return { monthly_income: this.monthlyIncome }
    }
  },

  watch: {
    $_navigation_triggerContinue: async function (val) {
      if (val) await this.handleContinue()
      this.$store.dispatch('setTriggerContinue', false)
    }
  },

  mounted () {
    this.submitted = false
  },

  methods: {
    async handleContinue () {
      this.submitted = true
      const isValid = await this.$_navigation_validateStep()
      if (!isValid) return
      this.$_loans_updateLead(this.updateLeadPayload, this.dataLayerObject)
    },
    handleInput (vuexAction, value) {
      // replace except digits, decimal points, and commas
      this.$_input_updateState(vuexAction, value.replace(/[^\d.,]/g, ''))
      if (this.submitted) {
        this.$_navigation_validateStep()
      }
    }
  }
}
</script>

<style scoped lang="scss">
</style>
